import React from 'react';
import { Card } from 'antd';
// Types
import { GetTicketEventSalesReport_ticketEventSalesReport_checkout } from 'api/ticketing/types/GetTicketEventSalesReport';
// Helpers
import { formatCount, pluralize } from 'pages/Events/helpers';
// Styles
import styles from './CheckoutFunnel.module.scss';

type Checkout = GetTicketEventSalesReport_ticketEventSalesReport_checkout;

type CheckoutFunnelProps = {
  checkout: Checkout;
};

const CheckoutFunnel = ({ checkout }: CheckoutFunnelProps): JSX.Element => {
  const {
    reservedBuyers,
    reservations,
    abandonedReservations,
    checkouts,
    paidOrders,
    declinedOrders,
    timedOutOrders,
    pendingOrders,
    payingBuyers,
  } = checkout;

  // Bars are sized against the people who reserved
  const barWidth = (value: number): string =>
    `${reservedBuyers ? Math.min(100, (value / reservedBuyers) * 100) : 0}%`;

  const steps = [
    {
      label: 'Reserved',
      value: reservedBuyers,
      caption: pluralize(reservations, 'reservation'),
    },
    { label: 'Reached payment', value: checkouts, caption: 'checkouts' },
    {
      label: 'Paid',
      value: paidOrders,
      caption: pluralize(payingBuyers, 'buyer'),
    },
  ];

  const dropOffs = [
    { label: 'Let their reservation run out', value: abandonedReservations },
    { label: 'Card declined', value: declinedOrders },
    { label: 'Payment timed out', value: timedOutOrders },
    { label: 'Paying now', value: pendingOrders },
  ].filter(({ value }) => value > 0);

  return (
    <Card title="Checkout" className={styles.card}>
      {reservations === 0 ? (
        <span className={styles.empty}>No one has started a checkout yet.</span>
      ) : (
        <>
          <div className={styles.steps}>
            {steps.map((step) => (
              <div key={step.label} className={styles.step}>
                <span className={styles.label}>{step.label}</span>
                <span className={styles.value}>{formatCount(step.value)}</span>
                <span className={styles.caption}>{step.caption}</span>
                <div className={styles.track}>
                  <div
                    className={styles.bar}
                    style={{ width: barWidth(step.value) }}
                  />
                </div>
              </div>
            ))}
          </div>

          {reservedBuyers > 0 && (
            <p className={styles.conversion}>
              {Math.round((payingBuyers / reservedBuyers) * 100)}% of the people
              who reserved went on to pay.
            </p>
          )}

          {dropOffs.length > 0 && (
            <ul className={styles.dropOffs}>
              {dropOffs.map(({ label, value }) => (
                <li key={label} className={styles.dropOff}>
                  <span>{label}</span>
                  <span className={styles.dropOffValue}>
                    {formatCount(value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <p className={styles.note}>Comps are not part of checkout.</p>
    </Card>
  );
};

export default CheckoutFunnel;
